'use client';

import { useEffect, useState, useRef, useMemo } from 'react';
import { useScheduler } from '../SchedulerContext';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Checkbox,
  Chip,
  Progress,
  Spinner,
  Tooltip,
} from '@heroui/react';
import { FaLightbulb, FaCheckCircle, FaPlus } from 'react-icons/fa';
import { FaBoxOpen, FaTag } from 'react-icons/fa6';
import ConfidenceChip, {
  getConfidenceColor,
  getConfidenceLabel,
} from '@/components/ui/ConfidenceChip';
import { MdError } from 'react-icons/md';
import { callAddonRecommendations } from '@/util/functions/fetching/callAddonRecommendations';
import { discountCoversService } from '@/util/functions/discountCodeValidator';
import { formatDiscountLabel } from '@/util/functions/formatDiscountLabel';
import { Service, Bundle } from '@/util/types/serverTypes';
import IconDisplay from '@/components/ui/IconDisplay';
import {
  bestCompleteBundle,
  bundleProgress,
  completingBundle,
  coveredServiceIds,
} from '@/util/functions/schedulingHelpers/bundleCoverage';

type PropertyAddonRecommendationsProps = {
  allServices: Service[];
  bundles: Bundle[];
  onContinue?: () => void;
};

export default function PropertyAddonRecommendations({
  allServices,
  bundles,
  onContinue,
}: PropertyAddonRecommendationsProps) {
  const {
    selectedPrimaryService,
    address,
    zillowPropertyData,
    containingServiceAreaArray,
    propertyDetails,
    aiRecommendations,
    setAiRecommendations,
    isLoadingAiRecommendations,
    setIsLoadingAiRecommendations,
    selectedAddons,
    setSelectedAddons,
    markPropertyPageSeen,
    selectedBundle: contextSelectedBundle,
    setSelectedBundle: setContextSelectedBundle,
    jwtToken,
    companyId,
    companyStyles,
    scheduleSettings,
    priceData,
    finalServices,
    appliedDiscountCode,
    pendingDiscountCode,
  } = useScheduler();

  /**
   * The discount code adding a service here could act on — the one already
   * applied, or one still waiting for a service that qualifies.
   *
   * This page is where a campaign link's promise is kept: the ad offered a
   * discount on a service the customer has not added, and these cards are where
   * they add it. Without a marker the qualifying service looks like every other
   * suggestion and the offer goes unclaimed.
   *
   * An `appliesToAllServices` code is deliberately excluded: it already covers
   * the whole catalog, so badging every card with it would be decoration rather
   * than a reason to pick one.
   */
  const discountInPlay = useMemo(() => {
    const code = appliedDiscountCode ?? pendingDiscountCode;
    return code && !code.appliesToAllServices ? code : null;
  }, [appliedDiscountCode, pendingDiscountCode]);

  /**
   * The upsell has to be shown before the Property step will release the
   * customer, and only the active internal page is rendered — so mounting is the
   * customer seeing it. Reported even while the recommendations are still
   * loading: they are on the page, which is what the gate asks for, and holding
   * the funnel hostage to a slow AI call would be worse than showing it late.
   */
  useEffect(() => {
    markPropertyPageSeen('addons');
  }, [markPropertyPageSeen]);

  // Primary's calculated price after modifiers (from same source as SchedulerSummaryBar)
  const basePrice =
    finalServices?.find((s) => s._id === selectedPrimaryService)?.totalPrice ??
    priceData?.totalPrice ??
    0;

  const [error, setError] = useState<string | null>(null);
  const [selectedRecommendations, setSelectedRecommendations] = useState<
    Set<string>
  >(new Set());
  const [selectedBundle, setSelectedBundle] = useState<string | null>(null);

  // Synchronous guard to prevent duplicate fetches within same render cycle
  // Context state is async; this ref provides immediate synchronous access
  const isFetchingRef = useRef(false);

  // Get the primary service object and its addon services
  const primaryService = allServices.find(
    (s) => s._id === selectedPrimaryService
  );
  const addonServiceIds = primaryService?._addonRecommendationIds || [];
  const allAddonServices = allServices
    .filter((s) => addonServiceIds.includes(s._id))
    .sort((a, b) => a.order - b.order);

  // Sync selectedRecommendations from context when selectedAddons changes
  useEffect(() => {
    // Initialize selectedRecommendations from context selectedAddons
    // Only include addons that are in the recommended addon services
    const relevantAddons = selectedAddons.filter((addonId) =>
      addonServiceIds.includes(addonId)
    );
    setSelectedRecommendations(new Set(relevantAddons));

    // Mark that we're initializing to prevent the sync-back effect from running
    isInitializingRef.current = true;
  }, [selectedAddons, addonServiceIds]);

  // Sync selectedBundle from context separately - only when contextSelectedBundle changes
  useEffect(() => {
    if (contextSelectedBundle !== null) {
      setSelectedBundle(contextSelectedBundle);
    }
  }, [contextSelectedBundle]);

  // Filter bundles where primary service is included in bundle items and are active
  const relevantBundles = useMemo(
    () =>
      bundles.filter(
        (bundle) =>
          bundle.active !== false &&
          bundle._primaryServiceId?.some(
            (item) => item === selectedPrimaryService
          )
      ),
    [bundles, selectedPrimaryService]
  );

  const lastProcessedBundleRef = useRef<string | null>(null);
  const isInitializingRef = useRef(true);

  // Clean up selectedRecommendations when a bundle is selected
  // Remove any services that are included in the selected bundle
  useEffect(() => {
    if (selectedBundle) {
      // Skip if we've already processed this bundle
      if (lastProcessedBundleRef.current === selectedBundle) {
        return;
      }

      const bundle = relevantBundles.find((b) => b._id === selectedBundle);
      if (bundle) {
        setSelectedRecommendations((prevRecs) => {
          const newRecs = new Set(prevRecs);
          let hasChanges = false;

          bundle.items.forEach((item) => {
            // Remove services that are in the bundle (except the primary service)
            if (item._serviceId !== selectedPrimaryService) {
              if (newRecs.has(item._serviceId)) {
                newRecs.delete(item._serviceId);
                hasChanges = true;
              }
            }
          });

          // Only return new set if there were actual changes
          return hasChanges ? newRecs : prevRecs;
        });

        // Mark this bundle as processed
        lastProcessedBundleRef.current = selectedBundle;
      }
    } else {
      // Reset ref when no bundle is selected
      lastProcessedBundleRef.current = null;
    }
  }, [selectedBundle, relevantBundles, selectedPrimaryService]);

  const selectedBundleItemIds = useMemo(() => {
    if (!selectedBundle) return [];
    return (
      relevantBundles
        .find((b) => b._id === selectedBundle)
        ?.items.map((item) => item._serviceId) ?? []
    );
  }, [selectedBundle, relevantBundles]);

  const coveredIds = useMemo(
    () =>
      coveredServiceIds(
        selectedPrimaryService,
        [...selectedRecommendations, ...selectedAddons],
        selectedBundleItemIds
      ),
    [
      selectedPrimaryService,
      selectedRecommendations,
      selectedAddons,
      selectedBundleItemIds,
    ]
  );

  // Auto-convert (or upgrade) when every service in a bundle is covered —
  // including services already claimed by a smaller selected bundle.
  useEffect(() => {
    if (isInitializingRef.current) return;

    const best = bestCompleteBundle(
      relevantBundles,
      coveredIds,
      selectedBundle
    );
    if (!best || best._id === selectedBundle) return;

    setSelectedBundle(best._id);
    setSelectedRecommendations((prev) => {
      const next = new Set(prev);
      best.items.forEach((item) => {
        if (item._serviceId !== selectedPrimaryService) {
          next.delete(item._serviceId);
        }
      });
      return next;
    });
  }, [
    coveredIds,
    selectedBundle,
    relevantBundles,
    selectedPrimaryService,
  ]);

  // Sync selectedRecommendations back to context selectedAddons
  // This ensures that when user unselects an addon, it's immediately reflected in context
  useEffect(() => {
    // Skip during initial sync to avoid overwriting context with stale local state
    if (isInitializingRef.current) {
      // Reset the flag after a brief delay to allow sync-in to complete
      setTimeout(() => {
        isInitializingRef.current = false;
      }, 0);
      return;
    }

    // Update context to only include addons that are:
    // 1. In the recommended addon services (addonServiceIds)
    // 2. Currently selected in local state (selectedRecommendations)
    const relevantSelectedAddons = Array.from(selectedRecommendations).filter(
      (addonId) => addonServiceIds.includes(addonId)
    );

    // Get current context addons that are NOT in this page's recommendations
    // (to preserve addons from other pages/steps)
    const otherAddons = selectedAddons.filter(
      (addonId) => !addonServiceIds.includes(addonId)
    );

    // Combine: other addons + relevant selected addons
    const newSelectedAddons = [...otherAddons, ...relevantSelectedAddons];

    // Only update if there's an actual change
    const currentRelevantAddons = selectedAddons.filter((addonId) =>
      addonServiceIds.includes(addonId)
    );
    const currentRelevantSet = new Set(currentRelevantAddons);
    const newRelevantSet = new Set(relevantSelectedAddons);

    if (
      currentRelevantSet.size !== newRelevantSet.size ||
      ![...currentRelevantSet].every((id) => newRelevantSet.has(id))
    ) {
      setSelectedAddons(newSelectedAddons);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecommendations, addonServiceIds]); // Exclude selectedAddons and setSelectedAddons to avoid loop

  // console.log({
  //   address,
  //   zillowPropertyData,
  //   allAddonServices,
  //   addonServiceIds,
  //   selectedRecommendations,
  //   aiRecommendations,
  //   relevantBundles,
  // });

  useEffect(() => {
    // Only fetch AI recommendations when we have all the required data
    // and aren't already loading or have results
    // isFetchingRef provides synchronous guard since context state updates are async
    if (
      !aiRecommendations &&
      !isLoadingAiRecommendations &&
      !isFetchingRef.current &&
      selectedPrimaryService &&
      zillowPropertyData !== null // Wait for Zillow data to load
    ) {
      // Set ref synchronously BEFORE calling async function
      isFetchingRef.current = true;
      fetchRecommendations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zillowPropertyData,
    // isLoadingAiRecommendations, 
    aiRecommendations]);

  const fetchRecommendations = async () => {
    // Double-check guards inside the function too
    if (!selectedPrimaryService || aiRecommendations) {
      return;
    }

    setIsLoadingAiRecommendations(true);
    setError(null);

    try {
      const recommendations = await callAddonRecommendations(
        selectedPrimaryService,
        address,
        zillowPropertyData,
        containingServiceAreaArray,
        propertyDetails,
        relevantBundles,
        jwtToken,
        companyId
      );

      setAiRecommendations(recommendations);
      // AI recommendations are shown but NOT auto-selected
      // User makes initial addon selections on ServiceConfirmation step
      // This step serves as an upsell opportunity with AI insights
    } catch (err) {
      console.error('[AIRecs] Fetch error:', err);
      // Provide user-friendly error message
      const errorMessage =
        err instanceof Error && err.message.includes('parse is not a function')
          ? 'AI recommendations service is temporarily unavailable. You can continue without AI suggestions.'
          : err instanceof Error
            ? err.message
            : 'Failed to load recommendations';
      setError(errorMessage);
      // Only reset ref on error to allow retry
      isFetchingRef.current = false;
    } finally {
      setIsLoadingAiRecommendations(false);
      // Don't reset isFetchingRef here - keep it true to prevent duplicate calls
    }
  };

  const handleToggleRecommendation = (serviceId: string) => {
    // If service is in a selected bundle, break apart the bundle
    if (isServiceInSelectedBundle(serviceId)) {
      const bundle = relevantBundles.find((b) => b._id === selectedBundle);
      if (bundle) {
        // Add all other bundle services (except the clicked one) as individual addons
        const otherBundleServices = bundle.items
          .map((item) => item._serviceId)
          .filter((id) => id !== serviceId && id !== selectedPrimaryService);

        setSelectedRecommendations((prev) => {
          const newSet = new Set(prev);
          otherBundleServices.forEach((id) => newSet.add(id));
          return newSet;
        });

        // Remove the bundle
        setSelectedBundle(null);
      }
      return;
    }

    // Normal toggle behavior
    setSelectedRecommendations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  const handleToggleBundle = (bundleId: string) => {
    // Toggle between null and the bundle ID (only one bundle allowed)
    if (selectedBundle === bundleId) {
      setSelectedBundle(null);
    } else {
      setSelectedBundle(bundleId);
      // Deselect any addons that are in this bundle
      const bundle = relevantBundles.find((b) => b._id === bundleId);
      if (bundle) {
        bundle.items.forEach((item) => {
          if (item._serviceId !== selectedPrimaryService) {
            setSelectedRecommendations((prevRecs) => {
              const newRecs = new Set(prevRecs);
              newRecs.delete(item._serviceId);
              return newRecs;
            });
          }
        });
      }
    }
  };

  const handleApplyRecommendations = () => {
    // Merge selected recommendations into selectedAddons
    const newAddons = Array.from(selectedRecommendations);
    setSelectedAddons((prev) => {
      const combined = new Set([...prev, ...newAddons]);
      return Array.from(combined);
    });

    // Save selected bundle to context
    if (selectedBundle) {
      setContextSelectedBundle(selectedBundle);
    }

    // Advance to next step
    onContinue?.();
  };

  const handleSkip = () => {
    // Just clear the selection, user can continue
    setSelectedRecommendations(new Set());
    setSelectedBundle(null);

    // Advance to next step
    onContinue?.();
  };

  const getAIRecommendation = (serviceId: string) => {
    return aiRecommendations?.recommendations.find(
      (rec) => rec.serviceId === serviceId
    );
  };

  const getAIBundleRecommendation = (bundleId: string) => {
    return aiRecommendations?.bundleRecommendations.find(
      (rec) => rec.bundleId === bundleId
    );
  };

  const isServiceInSelectedBundle = (serviceId: string) => {
    if (!selectedBundle) return false;
    const bundle = relevantBundles.find((b) => b._id === selectedBundle);
    return bundle?.items.some((item) => item._serviceId === serviceId) || false;
  };

  const getBundleDetails = (bundle: Bundle) => {
    const usePrimaryFee = scheduleSettings?.bundleDiscountDisplayPrimaryFee ?? true;

    const bundleServices = bundle.items
      .map((item) => allServices.find((s) => s._id === item._serviceId))
      .filter((s): s is Service => s !== undefined);

    // When bundleDiscountDisplayPrimaryFee is on, non-primary services use isPrimaryFee
    // as the "original" baseline so savings reflect the primary-fee comparison, not base fee.
    const originalTotal = bundleServices.reduce((sum, service) => {
      const isPrimaryService = service._id === selectedPrimaryService;
      const originalFee =
        usePrimaryFee && !isPrimaryService && service.isPrimaryFee != null
          ? service.isPrimaryFee
          : service.fee;
      return sum + originalFee;
    }, 0);

    // Discounted total is always derived from service.fee (the actual charged amount)
    const discountedTotal = bundle.items.reduce((sum, item) => {
      const service = allServices.find((s) => s._id === item._serviceId);
      if (!service) return sum;

      const discount =
        item.discountType === 'percent'
          ? service.fee * (item.discountValue / 100)
          : item.discountValue;

      return sum + (service.fee - discount);
    }, 0);

    const savings = originalTotal - discountedTotal;
    return { bundleServices, originalTotal, discountedTotal, savings };
  };

  const getBundleProgress = (bundle: Bundle) =>
    bundleProgress(
      bundle.items.map((item) => item._serviceId),
      coveredIds
    );

  const getCompletingBundle = (serviceId: string) => {
    const bundle = completingBundle(
      serviceId,
      relevantBundles,
      coveredIds,
      selectedBundle
    );
    if (!bundle) return null;
    const { savings } = getBundleDetails(bundle);
    return { bundle, savings };
  };

  if (isLoadingAiRecommendations) {
    return (
      <div className='flex min-h-[500px] items-center justify-center'>
        <div className='text-center'>
          <Spinner size='lg' />
          <p className='mt-4 text-lg font-medium'>
            Our AI is analyzing property data for optimal service
            recommendations...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='mx-auto max-w-3xl space-y-6'>
        <Alert
          color='danger'
          title='Failed to Load Recommendations'
          startContent={<MdError className='text-2xl' />}
        >
          <p>{error}</p>
        </Alert>
        <div className='flex justify-center gap-4'>
          <Button
            onPress={fetchRecommendations}
            className={companyStyles.secondaryButtonClassName}
            style={companyStyles.secondaryButtonStyles}
          >
            Try Again
          </Button>
          <Button variant='flat' onPress={handleSkip}>
            Skip AI Suggestions
          </Button>
        </div>
      </div>
    );
  }

  if (!selectedPrimaryService) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <Alert color='warning'>
          <p>
            Please select a primary service first to get additional service
            recommendations.
          </p>
        </Alert>
      </div>
    );
  }

  if (!aiRecommendations || !allAddonServices.length) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <p className='text-gray-500'>No additional services available</p>
      </div>
    );
  }

  console.log({ primaryService, relevantBundles, allAddonServices });

  return (
    <div className='mx-auto max-w-6xl space-y-6'>
      <div className='text-center'>
        <h4 className='mb-2'>AI-Recommended Additional Services</h4>
        <p className='text-sm text-gray-600'>
          Based on your property details, our AI suggests these additional
          services
        </p>
      </div>
      <div className='space-y-4 rounded-2xl  bg-linear-to-b from-white to-transparent p-4'>
        {/* Global Reason */}
        {aiRecommendations.globalReason && (
          <div className='space-y-3 px-3'>
            <div className='flex items-center gap-2'>
              <FaLightbulb
                className='text-2xl'
                style={{
                  color: companyStyles.companyColor || '#006FEE',
                }}
              />
              <h5 className='text-lg font-semibold'>AI Property Analysis</h5>
            </div>
            <p className='text-sm leading-relaxed text-gray-600'>
              {aiRecommendations.globalReason}
            </p>
          </div>
        )}

        {/* Primary Service Card */}


        {primaryService &&
          scheduleSettings?.showPrimaryServiceInAddonRecommendations && (
            <div className='px-2'>
              <Card className='border-2 bg-white' shadow='none'>
                <CardBody className='p-4'>
                  <div className='flex flex-col items-center justify-between gap-4 md:flex-row'>
                    <div className='flex items-center gap-4'>
                      {/* Icon */}
                      <div
                        className='flex h-16 w-16 shrink-0 items-center justify-center rounded-xl text-3xl'
                        style={{
                          backgroundColor:
                            companyStyles.secondaryButtonStyles
                              ?.backgroundColor || companyStyles.companyColor
                              ? `${companyStyles.secondaryButtonStyles
                                ?.backgroundColor ||
                              companyStyles.companyColor
                              }1A`
                              : 'rgba(0, 111, 238, 0.1)',
                          color:
                            companyStyles.secondaryButtonStyles
                              ?.backgroundColor ||
                            companyStyles.companyColor ||
                            '#006FEE',
                        }}
                      >
                        <IconDisplay
                          iconName={primaryService.icon}
                          size={40}
                          defaultIcon='FaScrewdriverWrench'
                        />
                      </div>
                      {/* Service Info */}
                      <div>
                        <div className='flex items-center gap-2'>
                          <h6 className='text-lg font-bold'>
                            {primaryService.name}
                          </h6>
                          {/* <Chip
                            size='sm'
                            color='primary'
                            variant='flat'
                            className='font-semibold'
                          >
                            Primary Service
                          </Chip> */}
                        </div>
                        {primaryService.description && (
                          <p className='mt-1 text-sm text-gray-600'>
                            {primaryService.description}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Price */}
                    {scheduleSettings?.showPricingInOnlineScheduler && (
                      <div className='text-right'>
                        <p className=' text-xs font-medium uppercase text-gray-500'>
                          Primary Service
                        </p>
                        <div
                          className='text-3xl font-bold'
                          style={{
                            color:
                              companyStyles.secondaryButtonStyles
                                ?.backgroundColor ||
                              companyStyles.companyColor ||
                              '#006FEE',
                          }}
                        >
                          {basePrice > 0 ? (
                            `$${basePrice.toFixed(2)}`
                          ) : (
                            <span className='text-2xl text-gray-400'>
                              Price TBD
                            </span>
                          )}
                        </div>
                        <p className='text-xxs text-gray-500'>
                          (No Additional Services)
                        </p>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>
          )}

        {/* Bundle Recommendations Section */}
        {relevantBundles.length > 0 && (
          <div className='space-y-4'>
            <h5 className='text-lg font-semibold'>Recommended Bundles</h5>
            {/* Drive columns from bundle count: 2 bundles fill the row, 3+
                shrink to a three-up size (and wrap beyond 3). lg engages
                within the max-w-6xl container, unlike the old xl. */}
            <div
              className={`grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 ${
                relevantBundles.length >= 3 ? 'lg:grid-cols-3' : ''
              }`}
            >
              {relevantBundles.map((bundle) => {
                const aiRec = getAIBundleRecommendation(bundle._id);
                const isSelected = selectedBundle === bundle._id;
                const { bundleServices, discountedTotal, savings } =
                  getBundleDetails(bundle);
                const { selectedCount, totalServices, remaining } =
                  getBundleProgress(bundle);
                const progressPercent = (selectedCount / totalServices) * 100;
                const isClose = remaining > 0 && remaining <= 2;

                return (
                  <Card
                    key={bundle._id}
                    isPressable
                    onPress={() => handleToggleBundle(bundle._id)}
                    className={`h-full border-2 border-warning bg-warning-50 shadow-warning/20 transition-all ${isSelected ? 'shadow-lg' : 'hover:border-warning'
                      }`}
                  >
                    <CardBody className='flex h-full flex-col space-y-3 overflow-hidden'>
                      {/* Top Row: Badge and Checkbox */}
                      <div className='flex items-start justify-between'>
                        {aiRec ? (
                          <Tooltip
                            content={
                              <div className='max-w-xs p-2'>
                                <p className='text-xs'>{aiRec.reason}</p>
                              </div>
                            }
                            delay={300}
                            closeDelay={100}
                            placement='top'
                            color='foreground'
                            showArrow
                          >
                            <Chip
                              color={getConfidenceColor(aiRec.confidence)}
                              size='sm'
                              variant='flat'
                              endContent={<FaLightbulb className='text-xs' />}
                            >
                              {getConfidenceLabel(aiRec.confidence)}
                            </Chip>
                          </Tooltip>
                        ) : (
                          <div />
                        )}
                        <Checkbox
                          isSelected={isSelected}
                          onValueChange={() => handleToggleBundle(bundle._id)}
                          size='lg'
                          color='warning'
                        />
                      </div>

                      {/* Bundle Info */}
                      <div className='space-y-2'>
                        <div className='flex items-center justify-between gap-2'>
                          <div className='flex items-center gap-2'>
                            <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-warning/20 text-lg text-warning'>
                              <FaBoxOpen />
                            </div>
                            <h6 className='wrap-break-word text-base font-bold'>
                              {bundle.name}
                            </h6>
                          </div>
                          <div className='shrink-0 text-right'>
                            {scheduleSettings?.showPricingInOnlineScheduler && (
                              <div className='text-lg font-bold text-warning'>
                                ${discountedTotal.toFixed(2)}
                              </div>
                            )}
                            {savings > 0 && (
                              <div
                                className={`${scheduleSettings?.showPricingInOnlineScheduler ? 'text-xs' : 'text-medium font-bold'} text-success`}
                              >
                                Save ${savings.toFixed(2)}
                              </div>
                            )}
                          </div>
                        </div>

                        {bundle.description && (
                          <p className='wrap-break-word text-xs text-gray-600'>
                            {bundle.description}
                          </p>
                        )}

                        {/* Progress indicator - only show if not selected and has some progress */}
                        {!isSelected && selectedCount > 0 && (
                          <div className='space-y-1 rounded-lg bg-white border p-2'>
                            <div className='flex items-center justify-between'>
                              <span className='text-xs font-medium text-gray-700'>
                                {selectedCount}/{totalServices} services
                                selected
                              </span>
                              {isClose && (
                                <span className='text-xs font-semibold text-success'>
                                  {remaining === 1
                                    ? 'Add 1 more to unlock!'
                                    : `Add ${remaining} more to unlock!`}
                                </span>
                              )}
                            </div>
                            <Progress
                              size='sm'
                              value={progressPercent}
                              color={isClose ? 'success' : 'warning'}
                              className='h-1.5'
                            />
                          </div>
                        )}

                        {/* Included Services */}
                        <div className='border-t pt-2'>
                          <p className='mb-1 text-xs font-semibold text-gray-700'>
                            Includes:
                          </p>
                          <div className='flex flex-wrap gap-1 overflow-hidden'>
                            {bundleServices.map((service) => {
                              const isServiceSelected = coveredIds.has(
                                service._id
                              );
                              return service ? (
                                <Chip
                                  key={service._id}
                                  size='sm'
                                  variant={isServiceSelected ? 'solid' : 'flat'}
                                  color={
                                    isServiceSelected ? 'success' : 'default'
                                  }
                                  className='shrink-0'
                                >
                                  {service.name}
                                </Chip>
                              ) : null;
                            })}
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Grid of all additional services */}
        <div className='space-y-4'>
          <h5 className='text-lg font-semibold'>
            Individual Additional Services
          </h5>
          <div className='grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4'>
            {allAddonServices.map((service) => {
              const aiRec = getAIRecommendation(service._id);
              const isSelected = selectedRecommendations.has(service._id);
              const isInBundle = isServiceInSelectedBundle(service._id);
              const completingBundle = getCompletingBundle(service._id);
              const unlocksDiscount = Boolean(
                discountInPlay &&
                  discountCoversService(discountInPlay, service._id)
              );

              return (
                <Card
                  key={service._id}
                  isPressable
                  onPress={() => handleToggleRecommendation(service._id)}
                  className={`transition-all ${isInBundle
                    ? 'border border-default-200 bg-default-100 opacity-60'
                    : completingBundle
                      ? 'border-2 border-warning bg-warning-50 ring-2 ring-warning/30'
                      : isSelected
                        ? 'border-2 border-success bg-success/5'
                        : 'border border-default-200 hover:border-default-400'
                    }`}
                >
                  <CardBody className='relative space-y-3 overflow-clip pb-4'>
                    {/* Completing bundle banner */}
                    {completingBundle && !isSelected && (
                      <div className='-mx-3 -mt-3 mb-1 bg-warning px-3 py-1.5 text-center'>
                        <p className='text-xs font-bold text-warning-foreground'>
                          🎁 Completes {completingBundle.bundle.name}
                        </p>
                        {completingBundle.savings > 0 && (
                          <p className='text-[10px] font-medium text-warning-foreground/80'>
                            Save ${completingBundle.savings.toFixed(2)}!
                          </p>
                        )}
                      </div>
                    )}

                    {/* Top Row: Badge (left) and Checkbox (right) */}
                    <div className='flex items-start justify-between'>
                      {isInBundle ? (
                        <Chip color='default' size='sm' variant='flat'>
                          Included in Bundle
                        </Chip>
                      ) : aiRec ? (
                        <Tooltip
                          content={
                            <div className='max-w-xs p-2'>
                              <p className='text-xs'>{aiRec.reason}</p>
                            </div>
                          }
                          delay={300}
                          closeDelay={100}
                          placement='top'
                          color='foreground'
                          showArrow
                        >
                          <Chip
                            color={getConfidenceColor(aiRec.confidence)}
                            size='sm'
                            variant='flat'
                            endContent={<FaLightbulb className='text-xs' />}
                          >
                            {getConfidenceLabel(aiRec.confidence)}
                          </Chip>
                        </Tooltip>
                      ) : (
                        <div />
                      )}
                      {unlocksDiscount && discountInPlay && (
                        <Tooltip
                          content={
                            <div className='max-w-xs p-2'>
                              <p className='text-xs'>
                                Discount code {discountInPlay.code} applies to
                                this service.
                              </p>
                            </div>
                          }
                          delay={300}
                          closeDelay={100}
                          placement='top'
                          color='foreground'
                          showArrow
                        >
                          <Chip
                            data-testid={`addon-discount-badge-${service._id}`}
                            color='success'
                            size='sm'
                            variant='flat'
                            startContent={<FaTag className='text-xs' />}
                          >
                            {formatDiscountLabel(discountInPlay)}
                          </Chip>
                        </Tooltip>
                      )}
                      <Checkbox
                        isSelected={isSelected || isInBundle}
                        onValueChange={() =>
                          handleToggleRecommendation(service._id)
                        }
                        size='lg'
                      />
                    </div>

                    {/* Center: Icon */}
                    <div className='flex justify-center py-2'>
                      <div
                        className='flex h-16 w-16 items-center justify-center rounded-xl text-3xl'
                        style={{
                          backgroundColor: companyStyles.companyColor
                            ? `${companyStyles.companyColor}1A`
                            : 'rgba(0, 111, 238, 0.1)',
                          color: companyStyles.companyColor || '#006FEE',
                        }}
                      >
                        <IconDisplay
                          iconName={service.icon}
                          size={48}
                          defaultIcon='FaScrewdriverWrench'
                        />
                      </div>
                    </div>

                    {/* Bottom: Title, Description, and Price */}
                    <div className='space-y-2'>
                      <div className='flex items-baseline justify-between gap-2'>
                        <Tooltip
                          content={service.name}
                          delay={300}
                          closeDelay={100}
                          placement='top'
                          color='foreground'
                          showArrow
                        >
                          <h6 className='min-w-0 truncate text-sm font-semibold'>
                            {service.name}
                          </h6>
                        </Tooltip>
                        {service.fee > 0 &&
                          scheduleSettings?.showPricingInOnlineScheduler && (
                            <span
                              className='shrink-0 text-sm font-medium'
                              style={{
                                color: companyStyles.companyColor || '#006FEE',
                              }}
                            >
                              ${service.fee.toFixed(2)}
                            </span>
                          )}
                      </div>
                      {service.description && (
                        <Tooltip
                          content={
                            <div className='max-w-sm p-2'>
                              <p className='text-xs'>{service.description}</p>
                            </div>
                          }
                          delay={300}
                          closeDelay={100}
                          placement='top'
                          color='foreground'
                          showArrow
                        >
                          <p className='line-clamp-2 cursor-help text-xs text-gray-600'>
                            {service.description}
                          </p>
                        </Tooltip>
                      )}
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className='flex justify-center gap-4 pt-4'>
          <Button
            size='lg'
            startContent={<FaCheckCircle />}
            onPress={handleApplyRecommendations}
            isDisabled={(() => {
              // Count only services that are NOT in the selected bundle
              const servicesNotInBundle = Array.from(
                selectedRecommendations
              ).filter((serviceId) => !isServiceInSelectedBundle(serviceId));
              return servicesNotInBundle.length === 0 && !selectedBundle;
            })()}
            className={companyStyles.secondaryButtonClassName}
            style={companyStyles.secondaryButtonStyles}
          >
            {(() => {
              // Count only services that are NOT in the selected bundle
              const servicesNotInBundle = Array.from(
                selectedRecommendations
              ).filter((serviceId) => !isServiceInSelectedBundle(serviceId));
              const additionalServicesCount = servicesNotInBundle.length;
              const bundleCount = selectedBundle ? 1 : 0;
              const hasSelection =
                bundleCount > 0 || additionalServicesCount > 0;

              if (!hasSelection) {
                return 'Continue';
              }

              return 'Continue With Services Selected';
            })()}
          </Button>
          <Button variant='flat' size='lg' onPress={handleSkip}>
            <span className='sm:hidden'>Skip</span>
            <span className='hidden sm:inline'>Skip Additional Services</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
